import React, { useState, useEffect, useRef } from "react";
import "./RequestGatepass.css";
import { Button, ImageWithFallback } from "../../components/Utilities";
import { apiService } from "../../../services/apiService";
import { useNavigate } from "react-router-dom";
import "../../components/ModelOverlay/RequestGatepass.css";
import "./UploadPickupProofModal.css";
import Delete from "../../assets/Delete.svg";
import { Loading } from "../../components/Loading"; // Import Loading component

// Utility function to convert file to base64 without prefix
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!(file instanceof File)) {
      console.error("fileToBase64: Invalid file input:", file);
      reject(new Error("Invalid file input: File object is required"));
      return;
    }
    console.log(
      "fileToBase64: Processing file:",
      file.name,
      file.type,
      file.size
    );
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result?.split(",")[1];
      if (!result) {
        reject(new Error("Failed to convert file to base64"));
      } else {
        resolve(result);
      }
    };
    reader.onerror = (error) => reject(error);
  });
};

// Validate file type and size
const validateFile = (file, allowedTypes, maxSizeMB = 5) => {
  if (!(file instanceof File)) {
    return "No file selected or invalid file input.";
  }
  if (!allowedTypes.includes(file.type)) {
    return `Invalid file type: ${file.type}. Only ${allowedTypes.join(", ")} are allowed.`;
  }
  if (file.size > maxSizeMB * 1024 * 1024) {
    return `File size exceeds ${maxSizeMB}MB limit.`;
  }
  return null;
};

// Upload Pickup Proof Modal Component
export const UploadPickupProofModal = ({
  isOpen,
  onClose,
  cars,
  selectedCars,
  onPhotosUploaded,
  isViewMode,
  onConfirmDelivery,
  showConfirmButton = true,
}) => {
  const [profileWithCarFile, setProfileWithCarFile] = useState(null);
  const [rcPhotoFile, setRcPhotoFile] = useState(null);
  const [error, setError] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false); // Separate loading for submit
  const [confirmLoading, setConfirmLoading] = useState(false); // Separate loading for confirm
  const navigate = useNavigate();
  const [profileUpdateTime, setProfileUpdateTime] = useState(null);
  const [rcUpdateTime, setRcUpdateTime] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const profileInputRef = useRef(null);
  const rcInputRef = useRef(null);

  // Load the latest delivery photos when modal opens
  useEffect(() => {
    if (isOpen) {
      const selectedCar = cars.find((car) => selectedCars.includes(car.id)) || {};
      const existingPhotos = selectedCar.delivery_photos || [];
      // Sort photos by uploadedAt to get the latest one for each label
      const sortedPhotos = existingPhotos.sort(
        (a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)
      );
      const profileWithCarPhoto = sortedPhotos.find(
        (photo) => photo.label === "Profile with car"
      ) || null;
      const rcPhoto = sortedPhotos.find(
        (photo) => photo.label === "RC photo"
      ) || null;
      setProfileWithCarFile(profileWithCarPhoto);
      setRcPhotoFile(rcPhoto);
      setError(null);
    } else {
      setProfileWithCarFile(null);
      setRcPhotoFile(null);
      setError(null);
      setProfileUpdateTime(null);
      setRcUpdateTime(null);
      setSubmitLoading(false);
      setConfirmLoading(false);
    }
  }, [isOpen, cars, selectedCars]);

  const handleProfileWithCarUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // const allowedTypes = ["image/jpeg", "image/png"];
    // const validationError = validateFile(file, allowedTypes);
    // if (validationError) {
    //   setError(validationError);
    //   return;
    // }

    // Revoke previous URL if it exists
    if (profileWithCarFile?.url && profileWithCarFile instanceof File) {
      URL.revokeObjectURL(profileWithCarFile.url);
    }

    const url = URL.createObjectURL(file);
    const newFile = { id: Date.now(), url, file, label: "Profile with car" };
    setProfileWithCarFile(newFile);
    setProfileUpdateTime(new Date().toLocaleString());
    setError(null);
    event.target.value = null; // Reset input
  };

  const handleRcPhotoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    // const validationError = validateFile(file, allowedTypes);
    // if (validationError) {
    //   setError(validationError);
    //   return;
    // }

    // Revoke previous URL if it exists
    if (rcPhotoFile?.url && rcPhotoFile instanceof File) {
      URL.revokeObjectURL(rcPhotoFile.url);
    }

    const url = file.type !== "application/pdf" ? URL.createObjectURL(file) : null;
    const newFile = { id: Date.now(), url, file, label: "RC photo" };
    setRcPhotoFile(newFile);
    setRcUpdateTime(new Date().toLocaleString());
    setError(null);
    event.target.value = null; // Reset input
  };

  const handleProfileWithCarDelete = () => {
    if (profileWithCarFile?.url && profileWithCarFile instanceof File) {
      URL.revokeObjectURL(profileWithCarFile.url);
    }
    setProfileWithCarFile(null);
    setProfileUpdateTime(null);
  };

  const handleRcPhotoDelete = () => {
    if (rcPhotoFile?.url && rcPhotoFile instanceof File) {
      URL.revokeObjectURL(rcPhotoFile.url);
    }
    setRcPhotoFile(null);
    setRcUpdateTime(null);
  };

  const handleSubmitPickupDetails = async (e) => {
    e.stopPropagation(); // Prevent event from bubbling up
    console.log("Submitting Pickup Details");
    if (!profileWithCarFile || !rcPhotoFile) {
      setError("Please upload both profile with car and RC photo.");
      return;
    }

    setSubmitLoading(true);
    setError(null);

    try {
      const selectedCar = cars.find((car) => selectedCars.includes(car.id));
      if (!selectedCar || !selectedCar.delivery_link_id) {
        throw new Error("Selected car or delivery link ID is missing.");
      }

      const deliveryLinkId = selectedCar.delivery_link_id;
      const uploadedAt = new Date().toISOString();
      // Start with existing photos, excluding those with labels we are updating
      const existingPhotos = (selectedCar.delivery_photos || []).filter(
        (photo) =>
          photo.label !== "Profile with car" && photo.label !== "RC photo"
      );
      const photoDetails = [...existingPhotos];

      // Include profile with car file if it exists
      if (profileWithCarFile?.file instanceof File) {
        const base64File = await fileToBase64(profileWithCarFile.file);
        const fileExtension = profileWithCarFile.file.type === "image/jpeg" ? "jpg" : "png";
        const fileName = `delivery_${deliveryLinkId}_${Date.now()}_Profile_with_car.${fileExtension}`;
        const uploadData = {
          file: base64File,
          folder: "delivery_photos",
          fileName,
          metadata: {
            contentType: profileWithCarFile.file.type,
            originalName: profileWithCarFile.file.name,
            size: profileWithCarFile.file.size,
            deliveryLinkId,
            label: "Profile with car",
            uploadedAt,
          },
        };

        console.log(
          "Uploading profile with car file:",
          JSON.stringify(uploadData, null, 2)
        );
        const response = await apiService.uploadFile(uploadData);
        console.log("Upload response:", JSON.stringify(response.data, null, 2));
        photoDetails.push({
          label: "Profile with car",
          url: response.data?.publicUrl || "",
          uploadedAt,
        });
      } else if (profileWithCarFile) {
        photoDetails.push(profileWithCarFile);
      }

      // Include RC photo file if it exists
      if (rcPhotoFile?.file instanceof File) {
        const base64File = await fileToBase64(rcPhotoFile.file);
        const fileExtension =
          rcPhotoFile.file.type === "application/pdf"
            ? "pdf"
            : rcPhotoFile.file.type === "image/jpeg"
            ? "jpg"
            : "png";
        const fileName = `delivery_${deliveryLinkId}_${Date.now()}_RC_photo.${fileExtension}`;
        const uploadData = {
          file: base64File,
          folder: "delivery_photos",
          fileName,
          metadata: {
            contentType: rcPhotoFile.file.type,
            originalName: rcPhotoFile.file.name,
            size: rcPhotoFile.file.size,
            deliveryLinkId,
            label: "RC photo",
            uploadedAt,
          },
        };

        console.log(
          "Uploading RC photo file:",
          JSON.stringify(uploadData, null, 2)
        );
        const response = await apiService.uploadFile(uploadData);
        console.log("Upload response:", JSON.stringify(response.data, null, 2));
        photoDetails.push({
          label: "RC photo",
          url: response.data?.publicUrl || "",
          uploadedAt,
        });
      } else if (rcPhotoFile) {
        photoDetails.push(rcPhotoFile);
      }

      // Update delivery photos in the database
      const updateData = {
        deliveryLinkId,
        photos: photoDetails,
      };

      console.log(
        "Updating delivery photos:",
        JSON.stringify(updateData, null, 2)
      );
      await apiService.updateDeliveryPhotos(updateData);
      console.log("Delivery photos updated successfully");

      // Update car state
      onPhotosUploaded(selectedCar.id, photoDetails);

      // Revoke object URLs
      if (profileWithCarFile?.url && profileWithCarFile instanceof File) {
        URL.revokeObjectURL(profileWithCarFile.url);
      }
      if (rcPhotoFile?.url && rcPhotoFile instanceof File && ["image/jpeg", "image/png"].includes(rcPhotoFile.file.type)) {
        URL.revokeObjectURL(rcPhotoFile.url);
      }

      setProfileWithCarFile(null);
      setRcPhotoFile(null);
      setProfileUpdateTime(null);
      setRcUpdateTime(null);
      onClose();
    } catch (err) {
      console.error("Error submitting pickup proof:", {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      let errorMessage = "Failed to submit pickup proof. Please try again.";
      if (err.code === "ECONNABORTED") {
        errorMessage = "Request timed out. Please check your network.";
      } else if (err.response?.status === 400) {
        errorMessage = `Invalid data provided: ${
          err.response.data?.message ||
          err.response.data?.error ||
          "Please check your uploaded files."
        }`;
      } else if (err.response?.status === 401) {
        errorMessage = "Unauthorized. Redirecting to login.";
        navigate("/login");
      } else if (err.response?.status === 500) {
        errorMessage = `Server error: ${
          err.response.data?.message || "Please try again later."
        }`;
      } else {
        errorMessage = `Failed to submit pickup proof: ${err.message}`;
      }
      setError(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleConfirmDelivery = (e) => {
    e.stopPropagation(); // Prevent event from bubbling up
    console.log("Confirming Delivery");
    if (isViewMode && onConfirmDelivery && selectedCars.length > 0) {
      setConfirmLoading(true);
      onConfirmDelivery(selectedCars[0]);
      onClose();
      setConfirmLoading(false); // Reset after action
    }
  };

  const handleImageClick = (image) => {
    if (image.url) {
      setSelectedImage(image.url);
      setIsImageModalOpen(true);
    }
  };

  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  if (!isOpen) return null;

  const selectedCar = cars.find((car) => selectedCars.includes(car.id)) || {};
  const areBothPhotosUploaded = profileWithCarFile && rcPhotoFile;

  return (
    <div className="upload-proof">
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="model-header">
            <p>{isViewMode ? "View Picture" : "Upload Pickup Proof"}</p>
            <button className="modal-close" onClick={onClose}>
              ×
            </button>
          </div>
          {error && <p className="error">{error}</p>}
          <div className="model-body">
            {submitLoading && <Loading />} {/* Add Loading component */}
            <div className="right-part">
              <div className="modal-title">
                <h4 className="car-title-modal">
                  {selectedCar.brand} {selectedCar.model} ({selectedCar.year})
                </h4>
                <p className="car-number">{selectedCar.rto}</p>
              </div>
            </div>
            <div className="left-part">
              <div className="profile-pic">
                {profileWithCarFile ? (
                  <div className="image-container">
                    <ImageWithFallback
                      src={profileWithCarFile.url}
                      alt="Profile with Car"
                      className="preview-image"
                      onClick={() => handleImageClick(profileWithCarFile)}
                    />
                    <ImageWithFallback
                      src={Delete}
                      alt="Delete Profile with Car"
                      className="delete-icon"
                      onClick={handleProfileWithCarDelete}
                    />
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      ref={profileInputRef}
                      accept="image/heic,image/heif,image/jpeg,image/jpg,image/png,image/gif,image/tiff,image/webp,image/bmp,image/x-adobe-dng,image/x-dng,application/pdf,video/mp4,video/quicktime,video/x-matroska,video/webm,video/3gpp,video/x-msvideo,video/hevc,video/h265"

                      // accept="image/jpeg,image/jpg,image/png"
                      onChange={handleProfileWithCarUpload}
                      style={{ display: "none" }}
                    />
                    <Button
                      variant="primary"
                      onClick={() => profileInputRef.current?.click()}
                      className="upload-button"
                    >
                      Upload Photo With Car*
                    </Button>
                  </div>
                )}
              </div>

              <div className="profile-pic">
                {rcPhotoFile ? (
                  <div className="image-container">
                    {["image/jpeg", "image/png"].includes(rcPhotoFile.file?.type || (rcPhotoFile.url && rcPhotoFile.url.includes(".jpg") ? "image/jpeg" : "image/png")) && (
                      <ImageWithFallback
                        src={rcPhotoFile.url}
                        alt="RC Photo"
                        className="preview-image"
                        onClick={() => handleImageClick(rcPhotoFile)}
                      />
                    )}
                    <ImageWithFallback
                      src={Delete}
                      alt="Delete RC Photo"
                      className="delete-icon"
                      onClick={handleRcPhotoDelete}
                    />
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      ref={rcInputRef}
                      accept="image/heic,image/heif,image/jpeg,image/jpg,image/png,image/gif,image/tiff,image/webp,image/bmp,image/x-adobe-dng,image/x-dng,application/pdf,video/mp4,video/quicktime,video/x-matroska,video/webm,video/3gpp,video/x-msvideo,video/hevc,video/h265"

                      // accept="image/jpeg,image/jpg,image/png,application/pdf"
                      onChange={handleRcPhotoUpload}
                      style={{ display: "none" }}
                    />
                    <Button
                      variant="primary"
                      onClick={() => rcInputRef.current?.click()}
                      className="upload-button"
                    >
                      Upload Photo of the RC*
                    </Button>
                  </div>
                )}
              </div>
            </div>

          </div>
                      {isViewMode ? (
              <>
                <Button
                  className="request-gate-pass-btn"
                  variant="primary"
                  onClick={handleSubmitPickupDetails}
                  disabled={submitLoading || !areBothPhotosUploaded}
                >
                  {submitLoading ? "Submitting..." : "Submit Pickup Details"}
                </Button>
              </>
            ) : (
              <Button
                className="request-gate-pass-btn"
                variant="primary"
                onClick={handleSubmitPickupDetails}
                disabled={submitLoading || !areBothPhotosUploaded}
              >
                {submitLoading ? "Submitting..." : "Submit Pickup Details"}
              </Button>
            )}
        </div>
      </div>

      {isImageModalOpen && selectedImage && (
        <div className="image-modal-overlay" onClick={handleCloseImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={handleCloseImageModal}>
              ×
            </button>
            <img
              src={selectedImage}
              alt="Full-size Preview"
              className="full-size-image"
            />
          </div>
        </div>
      )}
    </div>
  );
};